import { DeleteOutlined } from '@ant-design/icons'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { Button, List } from 'antd'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'

import {
  useTreatmentServiceDeletePotentialContactEndpoint,
  useTreatmentServiceGetListPotentialContactEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetListPotentialContactResponse,
  TreatmentService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { formErrorHandler } from 'src/utils'

import { ContactsListItem } from './ContactsListItem'
import { useStyles } from './styles'

export const ContactsForm = () => {
  const { styles } = useStyles()
  const queryClient = useQueryClient()

  const { id } = useParams()

  const containerRef = useRef<HTMLDivElement>(null)

  const { data, fetchNextPage, isFetching, hasNextPage } = useInfiniteQuery({
    queryKey: [useTreatmentServiceGetListPotentialContactEndpointKey, id],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await TreatmentService.getListPotentialContactEndpoint(
        String(id),
        pageParam,
        DEFAULT_PER_PAGE
      )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetListPotentialContactResponse
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const { mutate: deleteContact } =
    useTreatmentServiceDeletePotentialContactEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetListPotentialContactEndpointKey]
        })
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onDelete = (consultationId?: string, personId?: string) => {
    if (!consultationId || !personId) return

    deleteContact({
      consultationId,
      potentialContactId: personId,
      requestBody: { consultantId: consultationId }
    })
  }

  const contactsData = data?.pages.flatMap((page) => page.data) || []

  const handleScroll = () => {
    const container = containerRef.current
    if (!container || isFetching || !hasNextPage) return

    const { scrollTop, scrollHeight, clientHeight } = container

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      fetchNextPage()
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [isFetching, hasNextPage])

  return (
    <div ref={containerRef} className={styles.contactsList}>
      <List
        itemLayout="horizontal"
        dataSource={contactsData}
        className="full-width"
        loading={isFetching}
        renderItem={(contact) => (
          <ContactsListItem contact={contact}>
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => onDelete(contact?.consultantId, contact?.id)}
            />
          </ContactsListItem>
        )}
      />
    </div>
  )
}
